import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AuthLoginResult } from "../../../api";
import api from "../../../api";
import { AuthSession, UserPropertyOption } from "../auth";

const STORAGE_KEY = "passtrack-setup-auth";

interface StoredAuth {
  session: AuthSession;
  properties: UserPropertyOption[];
}

interface AuthContextValue {
  session: AuthSession | null;
  properties: UserPropertyOption[];
  isAuthenticated: boolean;
  login: (result: AuthLoginResult) => void;
  logout: () => void;
  refreshProperties: () => Promise<void>;
  getPropertyByDocumentId: (documentId: string) => UserPropertyOption | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredAuth(): StoredAuth | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as StoredAuth;
  } catch {
    return null;
  }
}

function writeStoredAuth(value: StoredAuth | null) {
  if (!value) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const stored = readStoredAuth();
  const [session, setSession] = useState<AuthSession | null>(
    stored?.session ?? null,
  );
  const [properties, setProperties] = useState<UserPropertyOption[]>(
    stored?.properties ?? [],
  );

  const login = useCallback((result: AuthLoginResult) => {
    const nextSession: AuthSession = {
      jwt: result.jwt,
      user: result.user,
    };

    setSession(nextSession);
    setProperties(result.properties);
    writeStoredAuth({
      session: nextSession,
      properties: result.properties,
    });
  }, []);

  const logout = useCallback(() => {
    setSession(null);
    setProperties([]);
    writeStoredAuth(null);
  }, []);

  const refreshProperties = useCallback(async () => {
    if (!session?.jwt || !session.user) {
      return;
    }

    const { user, properties: refreshedProperties } =
      await api.refreshUserSession(session.jwt, session.user);
    const nextSession: AuthSession = {
      jwt: session.jwt,
      user,
    };

    setSession(nextSession);
    setProperties(refreshedProperties);
    writeStoredAuth({
      session: nextSession,
      properties: refreshedProperties,
    });
  }, [session]);

  const getPropertyByDocumentId = useCallback(
    (documentId: string) =>
      properties.find((property) => property.documentId === documentId) ?? null,
    [properties],
  );

  const value = useMemo(
    () => ({
      session,
      properties,
      isAuthenticated: Boolean(session?.jwt),
      login,
      logout,
      refreshProperties,
      getPropertyByDocumentId,
    }),
    [session, properties, login, logout, refreshProperties, getPropertyByDocumentId],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
